import React from 'react';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, ArrowRight, ExternalLink, Navigation } from 'lucide-react';
import { Logo } from './Logo';
import { COMPANY } from '../mock';

const NAV_LINKS = [
  { label: 'Home', href: '#home' },
  { label: 'Services', href: '#services' },
  { label: 'Why Us', href: '#why' },
  { label: 'Case Studies', href: '#testimonials' },
  { label: 'FAQ', href: '#faq' },
];

const SERVICE_LINKS = [
  { label: 'Tender Bidding', href: '#services' },
  { label: 'Digital Growth', href: '#services' },
  { label: 'Bid Strategy & Analysis', href: '#services' },
  { label: 'SEO & Content', href: '#services' },
  { label: 'Compliance Support', href: '#services' },
];

const Footer = () => {
  const onClick = (e, href) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="relative pt-20 pb-10 px-6 lg:px-10 border-t border-white/5 overflow-hidden">
      {/* Soft glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[200px] bg-amber-500/5 blur-3xl rounded-full pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto">
        {/* CTA Banner */}
        <div className="glass-card rounded-3xl p-8 lg:p-12 mb-16 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <h3 className="text-2xl lg:text-3xl font-bold text-white mb-2">
              Ready to win your next tender?
            </h3>
            <p className="text-slate-400 max-w-xl">
              Let's talk about your goals — get a tailored proposal within 24 hours.
            </p>
          </div>
          <a
            href="#contact"
            onClick={(e) => onClick(e, '#contact')}
            className="inline-flex items-center gap-2 btn-amber rounded-full px-7 py-4 text-sm sm:text-base whitespace-nowrap"
          >
            Get a Free Quote
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Map + visit block */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          <div className="lg:col-span-2 glass-card rounded-3xl overflow-hidden relative h-[320px] sm:h-[360px]">
            <iframe
              title="Arvanta Solutions — Office Location"
              src="https://www.google.com/maps?q=Opal+Square+Alkapuri+Vadodara&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'grayscale(0.4) contrast(1.05) brightness(0.85)' }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/5 rounded-3xl" />
          </div>

          <div className="glass-card rounded-3xl p-7 flex flex-col">
            <div className="text-xs tracking-[0.25em] uppercase text-amber-400 font-semibold mb-4">
              Visit Our Office
            </div>
            <div className="flex items-start gap-3 mb-5">
              <span className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-amber-400" />
              </span>
              <div>
                <div className="font-semibold text-white text-base mb-1">Arvanta Solutions HQ</div>
                <div className="text-sm text-slate-300 leading-relaxed">{COMPANY.address}</div>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <a
                href={`tel:${COMPANY.phoneHref}`}
                className="flex items-center gap-3 text-sm text-slate-300 hover:text-white"
              >
                <Phone className="w-4 h-4 text-emerald-400" />
                {COMPANY.phone}
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                className="flex items-center gap-3 text-sm text-slate-300 hover:text-white"
              >
                <Mail className="w-4 h-4 text-blue-400" />
                {COMPANY.email}
              </a>
            </div>

            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Opal+Square+Alkapuri+Vadodara+390007"
              target="_blank"
              rel="noreferrer"
              className="mt-auto inline-flex items-center justify-center gap-2 btn-amber rounded-full px-5 py-3 text-sm w-full"
            >
              <Navigation className="w-4 h-4" />
              Get Directions
              <ExternalLink className="w-3.5 h-3.5 opacity-70" />
            </a>
          </div>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 mb-12">
          {/* Brand block */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <Logo size={42} />
              <div>
                <div className="text-white font-bold text-lg leading-tight">{COMPANY.name}</div>
                <div className="text-xs text-slate-400">{COMPANY.tagline}</div>
              </div>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed mb-6 max-w-sm">
              We help businesses win government and private tenders while accelerating digital
              growth through expert strategy and execution.
            </p>
            <div className="flex items-center gap-3">
              <a
                href="#"
                aria-label="LinkedIn"
                className="w-10 h-10 rounded-full btn-ghost-dark inline-flex items-center justify-center"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="#"
                aria-label="Twitter"
                className="w-10 h-10 rounded-full btn-ghost-dark inline-flex items-center justify-center"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href="#"
                aria-label="Facebook"
                className="w-10 h-10 rounded-full btn-ghost-dark inline-flex items-center justify-center"
              >
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div className="lg:col-span-2">
            <div className="text-xs tracking-[0.25em] uppercase text-amber-400 font-semibold mb-5">
              Company
            </div>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={(e) => onClick(e, l.href)}
                    className="text-sm text-slate-300 hover:text-white"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <div className="text-xs tracking-[0.25em] uppercase text-amber-400 font-semibold mb-5">
              Services
            </div>
            <ul className="space-y-3">
              {SERVICE_LINKS.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    onClick={(e) => onClick(e, l.href)}
                    className="text-sm text-slate-300 hover:text-white"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <div className="text-xs tracking-[0.25em] uppercase text-amber-400 font-semibold mb-5">
              Contact
            </div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="w-4 h-4 text-amber-400" />
                </span>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Opal+Square+Alkapuri+Vadodara+390007"
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-slate-300 hover:text-white leading-relaxed"
                >
                  {COMPANY.address}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-blue-400" />
                </span>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-sm text-slate-300 hover:text-white break-all"
                >
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-emerald-400" />
                </span>
                <a
                  href={`tel:${COMPANY.phoneHref}`}
                  className="text-sm text-slate-300 hover:text-white"
                >
                  {COMPANY.phone}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs text-slate-400">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </div>
          <div className="flex items-center gap-6 text-xs text-slate-400">
            <a href="#" className="hover:text-white">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white">
              Terms of Service
            </a>
            <a href="#" className="hover:text-white">
              Cookie Settings
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
